import MeaPage from "../../../components/MeaPage";
import { useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import contractsStatsAPI from "../../../services/contracts_stats";

const METHOD_LABELS = {
  CASH: "Espèces",
  CB: "Carte Bancaire",
  CHEQUE: "Chèque",
  HELLOASSO: "HelloAsso",
  OTHER: "Autre",
};

const METHOD_ICONS = {
  CASH: "pi pi-euro",
  CB: "pi pi-credit-card",
  CHEQUE: "pi pi-wallet",
  HELLOASSO: "pi pi-link",
  OTHER: "pi pi-align-justify",
};

const ContractsStatsPage = () => {
  const toast = useRef();
  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.registrationId;
  const { auth } = useAuthContext();
  const accessToken = auth?.access_token || "";

  // Fetch contracts statistics from new API
  const statsQuery = contractsStatsAPI.useGetContractsStats(
    schoolId,
    trainingId,
    accessToken
  );

  // Extract statistics from API response
  const statistics = statsQuery.data?.data || {
    totalContracts: 0,
    totalAmount: 0,
    totalReceived: 0,
    totalPaid: 0,
    totalRemaining: 0,
    monthlyData: []
  };
  const monthlyData = Array.isArray(statistics.monthlyData)
    ? statistics.monthlyData
    : [];

  const methodStats = statistics.methodStats || {};

  const methodChartData = useMemo(() => {
    const keys = Object.keys(methodStats).filter(k => methodStats[k] > 0);
    if (keys.length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [
          {
            data: [0],
            backgroundColor: ['#e2e8f0'],
            hoverBackgroundColor: ['#cbd5e1']
          }
        ]
      };
    }

    const documentStyle = getComputedStyle(document.documentElement);
    const colorsMapping = {
      CASH: {
        bg: documentStyle.getPropertyValue('--green-500') || '#22c55e',
        hover: documentStyle.getPropertyValue('--green-400') || '#4ade80'
      },
      CB: {
        bg: documentStyle.getPropertyValue('--blue-500') || '#3b82f6',
        hover: documentStyle.getPropertyValue('--blue-400') || '#60a5fa'
      },
      CHEQUE: {
        bg: documentStyle.getPropertyValue('--orange-500') || '#f97316',
        hover: documentStyle.getPropertyValue('--orange-400') || '#fb923c'
      },
      HELLOASSO: {
        bg: documentStyle.getPropertyValue('--purple-500') || '#a855f7',
        hover: documentStyle.getPropertyValue('--purple-400') || '#c084fc'
      },
      OTHER: {
        bg: documentStyle.getPropertyValue('--gray-500') || '#6b7280',
        hover: documentStyle.getPropertyValue('--gray-400') || '#9ca3af'
      }
    };

    const labels = keys.map(k => METHOD_LABELS[k] || k);
    const data = keys.map(k => methodStats[k]);
    const backgroundColor = keys.map(k => colorsMapping[k]?.bg || '#cbd5e1');
    const hoverBackgroundColor = keys.map(k => colorsMapping[k]?.hover || '#9ca3af');

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          hoverBackgroundColor
        }
      ]
    };
  }, [methodStats]);

  const methodChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true
        }
      }
    }
  };

  const methodDetails = useMemo(() => {
    const totalReceived = statistics.totalReceived || 0;
    return Object.keys(methodStats)
      .map(key => {
        const amount = methodStats[key];
        const percent = totalReceived > 0 ? ((amount * 100) / totalReceived).toFixed(1) : '0.0';
        return {
          key,
          name: METHOD_LABELS[key] || key,
          icon: METHOD_ICONS[key] || 'pi pi-wallet',
          amount,
          percent
        };
      })
      .filter(item => item.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [methodStats, statistics.totalReceived]);

  // Calculate dynamic chart height based on data points
  const getChartHeight = useMemo(() => {
    const dataLength = monthlyData.length;
    
    if (dataLength === 0) {
      return 200; // Minimum height for empty state
    }
    
    // Base height + additional height per data point
    const baseHeight = 200;
    const heightPerItem = 30;
    const maxHeight = 600; // Maximum height to prevent excessive scrolling
    
    const calculatedHeight = baseHeight + (dataLength * heightPerItem);
    return Math.min(calculatedHeight, maxHeight);
  }, [monthlyData.length]);

  // Calculate cumulative data for trend analysis
  const cumulativeData = useMemo(() => {
    if (monthlyData.length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [
          {
            label: 'Montant total',
            data: [0],
            borderColor: '#3b82f6', // blue-500
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.1
          },
          {
            label: 'Total reçu (cumulé)',
            data: [0],
            borderColor: '#a855f7', // purple-500
            backgroundColor: 'rgba(168, 85, 247, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1
          },
          {
            label: 'Total encaissé (cumulé)',
            data: [0],
            borderColor: '#22c55e', // green-500
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1
          }
        ]
      };
    }

    // Sort monthly data by sortKey to ensure proper chronological order
    const sortedMonthlyData = [...monthlyData].sort((a, b) => 
      a.sortKey.localeCompare(b.sortKey)
    );

    // Calculate cumulative values
    let cumulativeReceived = 0;
    let cumulativeEncaisses = 0;
    
    const cumulativeReceivedData = [];
    const cumulativeEncaissesData = [];
    const totalAmountData = [];

    sortedMonthlyData.forEach(item => {
      cumulativeReceived += item.received;
      cumulativeEncaisses += item.encaisses;
      
      cumulativeReceivedData.push(cumulativeReceived);
      cumulativeEncaissesData.push(cumulativeEncaisses);
      totalAmountData.push(statistics.totalAmount);
    });

    return {
      labels: sortedMonthlyData.map(item => item.month),
      datasets: [
        {
          label: 'Montant total',
          data: totalAmountData,
          borderColor: '#3b82f6', // blue-500
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.1
        },
        {
          label: 'Total reçu (cumulé)',
          data: cumulativeReceivedData,
          borderColor: '#a855f7', // purple-500
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.1
        },
        {
          label: 'Total encaissé (cumulé)',
          data: cumulativeEncaissesData,
          borderColor: '#22c55e', // green-500
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.1
        }
      ]
    };
  }, [monthlyData, statistics.totalAmount]);

  // Chart data for monthly histogram
  const chartData = useMemo(() => {
    if (monthlyData.length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [
          {
            label: 'Montants reçus',
            data: [0],
            backgroundColor: '#a855f7', // purple-500
            borderColor: '#9333ea', // purple-600
            borderWidth: 1
          },
          {
            label: 'Montants encaissés',
            data: [0],
            backgroundColor: '#22c55e', // green-500
            borderColor: '#16a34a', // green-600
            borderWidth: 1
          }
        ]
      };
    }

    // Sort monthly data by sortKey to ensure proper chronological order
    const sortedMonthlyData = [...monthlyData].sort((a, b) => 
      a.sortKey.localeCompare(b.sortKey)
    );

    return {
      labels: sortedMonthlyData.map(item => item.month),
      datasets: [
        {
          label: 'Montants reçus',
          data: sortedMonthlyData.map(item => item.received),
          backgroundColor: '#a855f7', // purple-500
          borderColor: '#9333ea', // purple-600
          borderWidth: 1
        },
        {
          label: 'Montants encaissés',
          data: sortedMonthlyData.map(item => item.encaisses),
          backgroundColor: '#22c55e', // green-500
          borderColor: '#16a34a', // green-600
          borderWidth: 1
        }
      ]
    };
  }, [monthlyData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Évolution des montants par mois'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString('fr-FR') + ' €';
          }
        }
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Évolution cumulative des montants'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString('fr-FR') + ' €';
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const StatCard = ({ title, value, icon, color, loading: cardLoading }) => (
    <Card className="text-center">
      <div className="flex flex-column align-items-center">
        <i className={`pi ${icon} text-4xl mb-3 ${color}`}></i>
        <h5 className="mb-2">{title}</h5>
        {cardLoading ? (
          <Skeleton width="100px" height="2rem" />
        ) : (
          <h3 className="m-0">{value}</h3>
        )}
      </div>
    </Card>
  );

  if (statsQuery.isLoading) {
    return (
      <>
        <Toast ref={toast} />
        <MeaPage 
          title="Statistiques des contrats" 
          subtitle="Analyse et statistiques des cotisations"
          showBackButton={true}
        >
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
              <Skeleton height="150px" />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <Skeleton height="150px" />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <Skeleton height="150px" />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <Skeleton height="150px" />
            </div>
            <div className="col-12">
              <Skeleton height="400px" />
            </div>
          </div>
        </MeaPage>
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <MeaPage 
        title="Statistiques des contrats" 
        subtitle="Analyse et statistiques des cotisations"
        showBackButton={true}
      >
        <div className="grid">
          {/* Summary Cards */}
          <div className="col-12 md:col-6 lg:col-3">
            <StatCard
              title="Total des contrats"
              value={statistics.totalContracts}
              icon="pi pi-file"
              color="text-gray-500"
              loading={statsQuery.isLoading}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-3">
            <StatCard
              title="Montant total"
              value={formatCurrency(statistics.totalAmount)}
              icon="pi pi-euro"
              color="text-blue-500"
              loading={statsQuery.isLoading}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-3">
            <StatCard
              title="Montant reçu"
              value={formatCurrency(statistics.totalReceived)}
              icon="pi pi-check-circle"
              color="text-purple-500"
              loading={statsQuery.isLoading}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-3">
            <StatCard
              title="Montant encaissé"
              value={formatCurrency(statistics.totalPaid)}
              icon="pi pi-credit-card"
              color="text-green-500"
              loading={statsQuery.isLoading}
            />
          </div>

          {/* Remaining Amount Card */}
          <div className="col-12">
            <Card>
              <div className="text-center">
                <i className="pi pi-exclamation-triangle text-4xl text-red-500 mb-3"></i>
                <h5 className="mb-2">Montant restant à recevoir</h5>
                <h2 className="text-red-500 m-0">{formatCurrency(statistics.totalRemaining)}</h2>
              </div>
            </Card>
          </div>

          {/* Répartition par type de paiement Card */}
          <div className="col-12">
            <Card>
              <h5 className="mb-3">Répartition par type de paiement</h5>
              <div className="grid align-items-center">
                <div className="col-12 md:col-6 flex justify-content-center p-4">
                  <Chart
                    type="doughnut"
                    data={methodChartData}
                    options={methodChartOptions}
                    height="220px"
                    style={{ height: '220px', width: '100%', maxWidth: '320px', position: 'relative' }}
                  />
                </div>
                <div className="col-12 md:col-6 p-4">
                  <div className="flex flex-column gap-3">
                    {methodDetails.map(item => (
                      <div key={item.key} className="flex align-items-center justify-content-between p-2 border-round hover:bg-gray-50 border-bottom-1 surface-border">
                        <div className="flex align-items-center gap-2">
                          <i className={`${item.icon} text-primary-500 text-lg`} />
                          <span className="font-semibold text-800">{item.name}</span>
                        </div>
                        <div className="flex align-items-center gap-3">
                          <span className="font-bold text-900">{formatCurrency(item.amount)}</span>
                          <Tag severity="info" value={`${item.percent}%`} className="font-semibold" />
                        </div>
                      </div>
                    ))}
                    {methodDetails.length === 0 && (
                      <div className="text-center p-3 text-500">
                        Aucun paiement encaissé ou en attente d'encaissement.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Monthly Chart */}
          <div className="col-12">
            <Card>
              <h5 className="mb-3">Évolution des montants par mois</h5>
              <Chart
                type="bar"
                data={chartData}
                options={chartOptions}
                height={`${getChartHeight}px`}
                style={{ height: `${getChartHeight}px`, position: 'relative' }}
              />
            </Card>
          </div>

          {/* Cumulative Trend Chart */}
          <div className="col-12">
            <Card>
              <h5 className="mb-3">Évolution cumulative des montants</h5>
              <Chart 
                key={`line-chart-${monthlyData.length}`}
                type="line" 
                data={cumulativeData} 
                options={lineChartOptions} 
                height="300px"
                style={{ height: '300px', width: '100%', position: 'relative' }}
              />
            </Card>
          </div>

          {/* Additional Statistics */}
          <div className="col-12 md:col-6">
            <Card>
              <h5 className="mb-3">Taux de collecte</h5>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-500 mb-2">
                  {statistics.totalAmount > 0 
                    ? ((statistics.totalReceived * 100) / statistics.totalAmount).toFixed(1)
                    : 0}%
                </div>
                <p className="text-600 m-0">du montant total collecté</p>
              </div>
            </Card>
          </div>

          <div className="col-12 md:col-6">
            <Card>
              <h5 className="mb-3">Taux d'encaissement</h5>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-500 mb-2">
                  {statistics.totalAmount > 0 
                    ? ((statistics.totalPaid / statistics.totalAmount) * 100).toFixed(1)
                    : 0}%
                </div>
                <p className="text-600 m-0">du montant total encaissé</p>
              </div>
            </Card>
          </div>
        </div>
      </MeaPage>
    </>
  );
};

export default ContractsStatsPage;
